import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { PartnerOnboardingProgress, PartnerOnboardingUpdate } from "@dearbill/shared";
import { useApi } from "../../auth/useApi";
import { usePartnerMe } from "../../auth/usePartnerMe";
import { useOrg } from "../../shell/OrgContext";
import { PharmacyOnboardingContext } from "./PharmacyOnboardingContext";

function isProgress(value: unknown): value is PartnerOnboardingProgress {
  if (typeof value !== "object" || value === null) return false;
  const state = value as Record<string, unknown>;
  return (
    state.version === 1 &&
    typeof state.status === "string" &&
    typeof state.step === "string" &&
    ["not_started", "in_progress", "deferred", "completed"].includes(state.status) &&
    ["welcome", "pharmacy_readiness", "working_with_patients", "next_action"].includes(
      state.step,
    ) &&
    (state.status !== "completed" || state.step === "next_action") &&
    (state.updatedAt === null || typeof state.updatedAt === "string")
  );
}

/** Scope the introduction to the signed-in staff member and current pharmacy. */
export function PharmacyOnboardingProvider({ children }: { children: ReactNode }): ReactNode {
  const { orgId, res } = useOrg();
  const { me, settled, error } = usePartnerMe();
  if (
    res.orgId !== orgId ||
    res.profile.organizationType !== "Pharmacy" ||
    !settled ||
    error ||
    me === null
  ) {
    return (
      <PharmacyOnboardingContext.Provider value={null}>
        {children}
      </PharmacyOnboardingContext.Provider>
    );
  }
  return (
    <MembershipIntroduction key={`${me.userId}:${orgId}`} orgId={orgId}>
      {children}
    </MembershipIntroduction>
  );
}

function MembershipIntroduction({
  orgId,
  children,
}: {
  orgId: string;
  children: ReactNode;
}): ReactNode {
  const api = useApi();
  const [progress, setProgress] = useState<PartnerOnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const mounted = useRef(false);
  const busy = useRef(false);
  const readRequest = useRef<AbortController | null>(null);
  const writeRequest = useRef<AbortController | null>(null);
  const endpoint = `/api/partner/orgs/${orgId}/onboarding`;

  const reload = useCallback((): void => {
    if (busy.current) return;
    readRequest.current?.abort();
    const request = new AbortController();
    readRequest.current = request;
    setLoading(true);
    setLoadError(false);
    api<unknown>(endpoint, { signal: request.signal })
      .then((next) => {
        if (!mounted.current || request.signal.aborted) return;
        if (!isProgress(next)) throw new Error("Invalid introduction state");
        setProgress(next);
      })
      .catch(() => {
        if (mounted.current && !request.signal.aborted) setLoadError(true);
      })
      .finally(() => {
        if (mounted.current && !request.signal.aborted) setLoading(false);
      });
  }, [api, endpoint]);

  useEffect(() => {
    mounted.current = true;
    reload();
    return () => {
      mounted.current = false;
      readRequest.current?.abort();
      writeRequest.current?.abort();
    };
  }, [reload]);

  const save = async (update: PartnerOnboardingUpdate): Promise<boolean> => {
    if (busy.current) return false;
    busy.current = true;
    setSaving(true);
    setSaveError(false);
    readRequest.current?.abort();
    setLoading(false);
    const request = new AbortController();
    writeRequest.current = request;
    try {
      const next = await api<unknown>(endpoint, {
        method: "PUT",
        body: { ...update },
        signal: request.signal,
      });
      if (!mounted.current || request.signal.aborted) return false;
      if (!isProgress(next)) throw new Error("Invalid introduction state");
      setProgress(next);
      return true;
    } catch {
      if (mounted.current && !request.signal.aborted) setSaveError(true);
      return false;
    } finally {
      busy.current = false;
      if (mounted.current && !request.signal.aborted) setSaving(false);
    }
  };

  return (
    <PharmacyOnboardingContext.Provider
      value={{ progress, loading, loadError, saving, saveError, reload, save }}
    >
      {children}
    </PharmacyOnboardingContext.Provider>
  );
}
